import { format, parse } from 'date-fns';
import { CONSTANTS } from '../components/Configuration';
import { isString } from './Utilities';
import * as locales from 'date-fns/locale';

/**
 * Converts a date string in the format "YYYY-MM-DDThh:mm:ss.000+hhmm" to a JavaScript Date object.
 *
 * @param {string} dateString - The date string to be converted.
 * @returns {Date|null} A Date object representing the converted date, or null if conversion fails.
 */
export const convertStringToDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  try {
    return new Date(dateString);
  }
  catch (err) {
    console.error(err);
    console.warn('Cannot convert string to date: ' + dateString);
    return null;
  }
};

/**
 * Formats a given date using the provided format string.
 *
 * @param {Date} date - The date to be formatted.
 * @param {string} formatString - The format string to be used for formatting.
 * @returns {string|null} - The formatted date as a string or null if date or formatString is missing.
 */
export const formatDateWithFormat = (date, formatString) => {
  if (date instanceof Date) {
    try {
      return format(date, formatString);
    } catch (error) {
      console.error(error);
      return format(date, CONSTANTS.dateFormatForUIyyyyMMdd);
    }
  } else {
    return null;
  }
};

/**
 * Parses a string date value using the provided format string and returns a Date object.
 *
 * @param {*} value - The date string to be parsed.
 * @param {string} formatString - The format string to be used for parsing.
 * @returns {Date|null} - The parsed Date object or null if value or formatString is missing.
 */
export const parsedDate = (value, formatString) => {
  if (value && isString(value)) {
    try {
      return parse(value, formatString, new Date());
    } catch (error) {
      console.error(error);
      return parse(value, CONSTANTS.dateFormatForUIyyyyMMdd, new Date());
    }
  } else {
    return null;
  }
};

/**
* Generate next date of param date as max date.
* Example param data is 31 Dec 2029, then max date will be 1 Jan 2030
* @return {Date} 31 Dec, 2099
*/
export function getDefaultMaxDate(date) {
  if (!date || date === CONSTANTS.dateMaxYear || !(date instanceof Date)) {
    date = new Date();
    date.setFullYear(CONSTANTS.dateMaxYear);
    date.setMonth(11, 31);
  } else {
    date = new Date(date.getTime() + (24 * 60 * 60 * 1000));
  }
  return date;
}

/**
* Generate previous date of param date as min date.
* Example param date is 31 Dec 2029, then min date will be 30 Dec 2029
* @return {Date} 31 Dec, 2099
*/
export function getDefaultMinDate(date) {
  if (date === CONSTANTS.dateMinYear || !date || !(date instanceof Date)) {
    date = new Date();
    date.setFullYear(CONSTANTS.dateMinYear - 1);
    date.setMonth(12, 1);
  } else {
    date = new Date(date.getTime() - (24 * 60 * 60 * 1000));
  }
  return date;
}

/**
 * This will calculate a max year based on the param date.
 * Max year will be year of the date param if it is valid and greater than CONSTANTS.dateMaxYear, otherwise CONSTANTS.dateMaxYear.
 *
 * @param {*} date - a valid date.
 * @returns return the max year.
 */
export const getMaxDateForDatePicker = (date) => {
  if (date instanceof Date) {
    return date.getFullYear() > CONSTANTS.dateMaxYear ? date.getFullYear() : CONSTANTS.dateMaxYear;
  } else {
    return CONSTANTS.dateMaxYear;
  }
};

/**
 * Get a data format pattern based on user current locale.
 * Example -  dd/MM/yyyy, MM/dd/yyyy etc.
 *
 * @returns Date format pattern string
 */
export const getDatePattern = () => {

  const locale = getUserLocale();
  const rootLocale = locale.substring(0, 2);
  let localeObject = Object.values(locales).find(item => item.code === locale);
  localeObject = localeObject ? localeObject : Object.values(locales).find(item => item.code === rootLocale);
  const formatLocal = localeObject?.formatLong?.date({ width: 'medium' });
  return formatLocal ? formatLocal : CONSTANTS.dateFormatForUI;
};

/**
 * Get a user locale string.
 * Examples - hi-IN, eng-IN, eng, ar etc.
 *
 * @returns user locale string.
 */
export const getUserLocale = () => {
  return navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language;
};

/**
 * Get the default date with default time
 * @param {*} date
 * @returns the default date
 */
export const getDateWithDefaultTime = (date) => {
  return new Date(date + 'T00:00');
};
